import React, { useState } from 'react';
import HelpAccordion from './HelpAccordion';

const StudentGuide: React.FC = () => (
    <div className="space-y-2">
        <HelpAccordion title="How do I use the lesson grid?">
            <p>Your lessons are organized in a grid. This makes it easy to see your progress! Here's what the different styles mean:</p>
            <ul className="list-disc list-inside mt-2 space-y-2">
                <li><strong className="text-blue-600">Unlocked (Bright & Colorful):</strong> This is your next lesson! Click on it to start typing.</li>
                <li><strong className="text-green-600">Completed (Green Checkmark):</strong> Great job! You've already finished this lesson. You can always go back and practice it again.</li>
                <li><strong className="text-gray-500">Locked (Grayed out with a lock icon):</strong> You need to complete the previous lessons before you can unlock this one. Keep going, you're almost there!</li>
            </ul>
        </HelpAccordion>

        <HelpAccordion title="What is the Checkpoint Rocket?">
            <p>
                Every 5 lessons, instead of jumping straight to the next one, you'll hit a <strong>Checkpoint</strong>: a quick rocket-launch game built entirely from the keys and words you just practiced. Type 10 words correctly to launch your rocket and see your accuracy, WPM, and a star rating. You can always hit "Try Again" before continuing -- it's a recap, not a test you can fail.
            </p>
        </HelpAccordion>

        <HelpAccordion title="How do I play the games?">
             <p>
                Navigate to the <strong>Games</strong> tab from your dashboard. Each game is built to train a specific skill -- reflexes, speed, accuracy, vocabulary -- and the card tells you which. Just click "Play" on any card to start! <strong>Multiplayer Race</strong> needs you to be enrolled in a class managed by your teacher, since it races you against your classmates live.
            </p>
        </HelpAccordion>

        <HelpAccordion title="How can I see my progress?">
            <p>
                Click on the <strong>My Progress</strong> tab. Here you can find:
            </p>
            <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Your overall <strong>curriculum progress</strong> (how many of the 670 tutorial lessons you've completed) and your current daily <strong>streak</strong>.</li>
                <li>An <strong>activity chart</strong> of the last 14 days and a <strong>key hotspots</strong> map showing which keys trip you up most.</li>
                <li>Your best scores across every game you've played.</li>
                <li>A chart and detailed report of <strong>WPM</strong> and <strong>accuracy</strong> for any homework or custom lessons your teacher assigned.</li>
                <li>Any badges you've earned for reaching milestones.</li>
            </ul>
        </HelpAccordion>

        <HelpAccordion title="I forgot my password -- what do I do?">
            <p>
                You can't reset your own password. Ask your teacher -- they can set you a new one from their <strong>Class Management</strong> tab (look for the "Reset Password" action next to your username) and let you know what it is.
            </p>
        </HelpAccordion>
    </div>
);

const TeacherGuide: React.FC = () => (
    <div className="space-y-2">
        <HelpAccordion title="How do I create a class and add students?">
            <p>Students in Typing Masters never have their real name entered into the system -- each one is identified only by a short, anonymous username. Here's how it works:</p>
            <ol className="list-decimal list-inside mt-2 space-y-2">
                <li>From your dashboard, go to the <strong>Class Management</strong> tab and create a class by entering a name.</li>
                <li>Select your new class from the dropdown, then set a <strong>username prefix</strong> for it (3-5 letters/numbers, e.g. "cls5"). Every student you add to this class will get that prefix plus the next number: "cls51", "cls52", and so on.</li>
                <li>Add students <strong>one at a time</strong> or in <strong>bulk</strong> (just pick how many to create -- no names needed).</li>
                <li>Choose a password: by default every student in the class shares <strong>one password you set</strong>, which is usually easiest for younger students to remember. You can also let the system generate a unique password per student instead.</li>
                <li><strong>Important:</strong> A modal will show the generated usernames and passwords once. Copy or download them and hand them out to your students -- there's no other way to see them again except by resetting a password later.</li>
            </ol>
        </HelpAccordion>

        <HelpAccordion title="How do I create a custom lesson?">
            <p>Go to the <strong>Lesson Builder</strong> tab, give your lesson a title, and type or paste the practice text you want your students to type. Save it and it becomes available to assign to any of your classes alongside the built-in tutorial curriculum.</p>
        </HelpAccordion>

        <HelpAccordion title="How do I assign lessons to a class?">
             <ol className="list-decimal list-inside mt-2 space-y-2">
                <li>First, make sure you have selected the correct class from the dropdown menu on your dashboard.</li>
                <li>Go to the <strong>Assignments</strong> tab.</li>
                <li>On the left, you will see a dropdown menu of all available lessons (both system lessons and ones you've created).</li>
                <li>Select a lesson you wish to assign and click the "Assign" button.</li>
                <li>The lesson will now appear in the "Assigned" list on the right and will be visible to all students in that class as homework.</li>
             </ol>
        </HelpAccordion>

        <HelpAccordion title="How can I analyze student performance?">
            <p>Our analytics tools give you deep insights into your class's performance.</p>
             <ul className="list-disc list-inside mt-2 space-y-2">
                <li>On the <strong>Analytics</strong> tab, you'll find a <strong>Keyboard Heatmap</strong>. This shows the most commonly missed keys across your entire class, with darker red keys indicating more errors.</li>
                <li>The <strong>Student-Specific Analysis</strong> table highlights individual students who are struggling and shows their single most-missed key. Click a student's name for a full report: curriculum progress, activity streak, their own key hotspots, game scores, and every homework grade you've given them.</li>
            </ul>
        </HelpAccordion>

        <HelpAccordion title="How do I invite a parent to view their child's progress?">
            <p>There's no automatic email sent -- you get a link to share with the parent yourself (text, email, whatever's easiest).</p>
            <ol className="list-decimal list-inside mt-2 space-y-2">
                <li>Go to the <strong>Student Progress</strong> tab and find the student you wish to connect a parent to.</li>
                <li>If the student's "Parent Status" is "Not Linked", click the "Invite" button in the Actions column.</li>
                <li>Enter the parent's email address and click "Create Invitation". You'll get a one-time link -- copy it and send it to the parent yourself.</li>
                <li>Once the parent opens that link and creates their account, they're automatically linked to their child and can view their progress.</li>
            </ol>
        </HelpAccordion>

        <HelpAccordion title="What are Live Sessions?">
             <p>
                Live Sessions allow you to generate a temporary code that gives guests (like students in a computer lab who don't have accounts) access to a specific lesson or set of lessons.
            </p>
             <ol className="list-decimal list-inside mt-2 space-y-2">
                <li>Go to the <strong>Live Sessions</strong> tab.</li>
                <li>Select the content you want to share (e.g., Tutorials 1-10 or a custom lesson you made).</li>
                <li>Click "Generate 2-Hour Code".</li>
                <li>Share the generated code or direct link with your guests. They can go to the "Join With Code" page on the website to start their temporary session.</li>
            </ol>
        </HelpAccordion>

        <HelpAccordion title="A student or parent forgot their password -- what do I do?">
            <p>
                For a <strong>student</strong>: open <strong>Class Management</strong>, find them in the roster, and click "Reset Password" -- you'll get a new password to give them.
            </p>
            <p className="mt-2">
                For a <strong>parent</strong> (or if you forgot your own password): that's handled by your school administrator, since teacher and parent passwords aren't self-service either.
            </p>
        </HelpAccordion>
    </div>
);

const ParentGuide: React.FC = () => (
    <div className="space-y-2">
        <HelpAccordion title="How do I get access to my child's progress?">
            <p>
                Your child's teacher sends you an invitation link (by text, email, or however they choose to share it). Open that link, fill in your name and a password to create your account, and you'll be automatically linked to your child -- no separate approval step needed.
            </p>
        </HelpAccordion>

        <HelpAccordion title="What can I see on my dashboard?">
            <p>Once logged in, you'll see the same detailed report your child's teacher sees:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Overall curriculum progress and current activity streak.</li>
                <li>A 14-day activity chart and a "key hotspots" map of the letters they mix up most.</li>
                <li>Scores from any games they've played.</li>
                <li>WPM/accuracy trends and grades for homework or custom lessons their teacher assigned.</li>
                <li>Badges they've earned.</li>
            </ul>
        </HelpAccordion>

        <HelpAccordion title="I have more than one child on the platform -- can I see them all?">
            <p>Yes. Use the "Viewing Progress For" dropdown at the top of your dashboard to switch between any children you're linked to.</p>
        </HelpAccordion>

        <HelpAccordion title="I forgot my password -- what do I do?">
            <p>Contact your child's school administrator -- they can reset it for you from their admin dashboard.</p>
        </HelpAccordion>
    </div>
);

const StaffGuide: React.FC = () => (
    <div className="space-y-2">
        <HelpAccordion title="What can a School Administrator do?">
            <p>From the <strong>School Admin Dashboard</strong> you can:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
                <li>See school-wide stats: total students, teachers, and average WPM/accuracy on the <strong>Overview</strong> tab.</li>
                <li>View and manage every <strong>Teacher</strong>, <strong>Student</strong>, and <strong>Parent</strong> account at your school, including resetting passwords.</li>
                <li>Review and approve or decline new teacher sign-ups on the <strong>Approvals</strong> tab.</li>
            </ul>
        </HelpAccordion>

        <HelpAccordion title="What can a Super Admin (platform administrator) do?">
            <p>The <strong>Super Admin Dashboard</strong> manages the whole platform, across every school:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
                <li><strong>Manage Schools</strong> -- add or remove schools registered on the platform.</li>
                <li><strong>Manage Admins</strong> -- create school administrator accounts and reset their passwords.</li>
                <li><strong>Manage Teachers</strong> -- see every teacher account across all schools.</li>
                <li><strong>Audit Log</strong> -- a searchable record of sensitive actions (logins, password resets, deletions, grade changes) for accountability.</li>
            </ul>
        </HelpAccordion>

        <HelpAccordion title="I forgot my password -- what do I do?">
            <p>
                School Administrators: ask your platform (super) administrator to reset it. Super Administrators: contact platform support directly, since there's no account above yours to do it for you.
            </p>
        </HelpAccordion>
    </div>
);


const HelpCenterPage: React.FC = () => {
    const [activeTab, setActiveTab] = useState<'students' | 'teachers' | 'parents' | 'staff'>('students');

    const tabs: { key: typeof activeTab; label: string }[] = [
        { key: 'students', label: 'For Students' },
        { key: 'teachers', label: 'For Teachers' },
        { key: 'parents', label: 'For Parents' },
        { key: 'staff', label: 'For School Staff' },
    ];

    const renderGuide = () => {
        switch (activeTab) {
            case 'teachers': return <TeacherGuide />;
            case 'parents': return <ParentGuide />;
            case 'staff': return <StaffGuide />;
            case 'students':
            default: return <StudentGuide />;
        }
    };

    return (
        <div className="w-full max-w-4xl bg-white p-6 md:p-8 rounded-lg shadow-lg animate-fade-in-up">
            <h1 className="text-4xl font-bold text-center text-gray-800 mb-8">Help Center</h1>
            <div className="flex flex-wrap justify-center border-b border-gray-200 mb-8">
                {tabs.map(t => (
                    <button
                        key={t.key}
                        onClick={() => setActiveTab(t.key)}
                        className={`py-2 px-6 font-semibold text-lg transition-colors ${activeTab === t.key ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-blue-600'}`}
                        aria-pressed={activeTab === t.key}
                    >
                        {t.label}
                    </button>
                ))}
            </div>
            <div>
                {renderGuide()}
            </div>
        </div>
    );
};

export default HelpCenterPage;
